// src/app/services/auth.service.ts

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, tap, timeout } from 'rxjs/operators';
import { GlobalMtmUserDTO } from '../../model/user.register';
import { StorageService } from '../storage.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private userRoleKey = 'userRole';
  private userIdKey = 'userId';
  private accessTokenKey = 'access_token';
  private isTherapistKey = 'isTherapist';
  private allowToSubmitKey = 'allowToSubmit';
  private userClinicsKey = 'userClinics';
  private selectedClinicIdKey = 'selectedClinicId';
  private selectedClinicNameKey = 'selectedClinicName';
  private readonly REQUEST_TIMEOUT = 100000; // 10 segundos
  
  constructor(
    private http: HttpClient,
    private storageService: StorageService
  ) {}

  setUserRole(role: string, userId: string): void {
    this.storageService.setItem(this.userRoleKey, role);
    this.storageService.setItem(this.userIdKey, userId);
  }

  getUserRole(): string | null {
    return this.storageService.getItem(this.userRoleKey);
  }

  getUserSessionId(): number | null {
    const item = this.storageService.getItem(this.userIdKey);
    return item !== null ? Number(item) : null;
  }

  getAccessToken(): string {
    const item = this.storageService.getItem(this.accessTokenKey);
    return item !== null ? item : "";
  }

  setAccessToken(accessToken: string): void {
    this.storageService.setItem(this.accessTokenKey, accessToken);
  }

  resetAccessToken(): void {
    const item = this.storageService.getItem(this.accessTokenKey);

    if (item !== null)
      this.storageService.setItem(this.accessTokenKey, "");

    // Clear role/submission context so a stale therapist flag can't leak into the next
    // session on a shared browser.
    this.clearUserContext();
  }

  isSuperAdmin(): boolean {
    return this.getUserRole() === 'Super Admin';
  }

  isCountryAdmin(): boolean {
    return this.getUserRole() === 'Country Admin';
  }

  isTherapist(): boolean {
    return this.storageService.getItem(this.isTherapistKey) === 'true';
  }

  allowToSubmitOrders(): boolean {
    const val = this.storageService.getItem(this.allowToSubmitKey);
    return val === null ? true : val === 'true';
  }

  setUserContext(isTherapist: boolean, allowToSubmit: boolean): void {
    this.storageService.setItem(this.isTherapistKey, isTherapist.toString());
    this.storageService.setItem(this.allowToSubmitKey, allowToSubmit.toString());
  }

  clearUserContext(): void {
    this.storageService.removeItem(this.isTherapistKey);
    this.storageService.removeItem(this.allowToSubmitKey);
    this.storageService.removeItem(this.userClinicsKey);
    this.storageService.removeItem(this.selectedClinicIdKey);
    this.storageService.removeItem(this.selectedClinicNameKey);
  }

  getClinics(): { id: number; name: string }[] {
    const raw = this.storageService.getItem(this.userClinicsKey);
    if (!raw) return [];
    try { return JSON.parse(raw); } catch { return []; }
  }

  setClinics(clinics: { id: number; name: string }[]): void {
    this.storageService.setItem(this.userClinicsKey, JSON.stringify(clinics));
    if (clinics.length > 0 && !this.storageService.getItem(this.selectedClinicIdKey)) {
      this.setSelectedClinic(clinics[0].id, clinics[0].name);
    }
  }

  getSelectedClinic(): { id: number; name: string } | null {
    const idRaw = this.storageService.getItem(this.selectedClinicIdKey);
    const name = this.storageService.getItem(this.selectedClinicNameKey);
    if (!idRaw || !name) return null;
    return { id: Number(idRaw), name };
  }

  setSelectedClinic(id: number, name: string): void {
    this.storageService.setItem(this.selectedClinicIdKey, id.toString());
    this.storageService.setItem(this.selectedClinicNameKey, name);
  }

  // Método para registro
  register(user: GlobalMtmUserDTO): Observable<any> {
    const headers = this.getHeaders();
    const endpoint = `${environment.api}/v1/partners/register`;

    return this.http.post(endpoint, user, { headers }).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError('Error to register user'))
    );
  }

  basicUserConfirmation(token: string): Observable<any> {
    const headers = this.getHeaders();
    const endpoint = `${environment.api}/v1/authentication/confirmations?token=${token}`;

    return this.http.post(endpoint, {}, { headers }).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError('Error confirming user'))
    );
  }

  userValidation(): Observable<any> {
    const headers = this.getHeaders();
    const endpoint = `${environment.api}/v1/authentication/validations`;

    return this.http.post<any>(endpoint, {}, { headers }).pipe(
      timeout(this.REQUEST_TIMEOUT),
      tap(response => {
        if (response?.data) {
          this.setUserContext(
            response.data.isTherapist === true,
            response.data.allowToSubmit !== false
          );
          if (Array.isArray(response.data.clinics)) {
            this.setClinics(response.data.clinics);
          }
        }
      }),
      catchError(this.handleError('Error validating user'))
    );
  }

  registerTherapistViaInvite(payload: { inviteToken: string; password: string; organization: string; phone: string; personalData: string }): Observable<any> {
    const headers = this.getHeaders();
    const endpoint = `${environment.api}/v1/therapists/register`;

    return this.http.post(endpoint, payload, { headers }).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError('Error registering therapist'))
    );
  }

  acceptTherapistInvite(inviteToken: string): Observable<any> {
    const headers = this.getHeaders();
    const endpoint = `${environment.api}/v1/therapists/accept-invite`;

    return this.http.post(endpoint, { inviteToken }, { headers }).pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError(this.handleError('Error accepting therapist invite'))
    );
  }

  // Optional: Method to handle token/session storage
  private setSession(token: string, userId: string, role: string): void {
    this.storageService.setItem('access_token', token);
    this.setUserRole(role, userId);
  }

  // Optional: Error handling method
  private handleError(operation: string) {
    return (error: any): Observable<never> => {
      console.error(`${operation}:`, error);

      // Combine all error messages with a space
      const errorMessage = (error.error.errors || []).join(' ');

      return throwError(() => new Error(errorMessage));
    };
  }

  getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Ocp-Apim-Subscription-Key': environment.apiKey,
      'Authorization': "Bearer " + this.getAccessToken()
    });
  }
}